import React, { FC, ReactElement, useRef } from 'react';
import { useSelector } from 'react-redux';
import s from './ChoosePrompt.module.css';
import { BlockName } from '../../../../shared/types/projectTypes';
import { toggleActivePrompt } from '../../../../store/slices/pageSlice';
import { selectActivePrompt } from '../../../../store/selectors/pageSelectors';
import IconTone from '../../../../shared/ui/icons/IconTone';
import IconVoice from '../../../../shared/ui/icons/IconVoice';
import IconImproveText from '../../../../shared/ui/icons/IconImproveText';
import IconEditPrompt from '../../../../shared/ui/icons/IconEditPrompt';
import IconSuggestions from '../../../../shared/ui/icons/IconSuggestions';
import IconHashtagsx from '../../../../shared/ui/icons/IconHashtag';
import IconUpload from '../../../../shared/ui/icons/IconUpload';
import IconChatBot from '../../../../shared/ui/icons/IconChatBot';
import IconReferences from '../../../../shared/ui/icons/IconReferences';

import PremiumFeatureTooltip from '../premium-feature-tooltip/PremiumFeatureTooltip';
import IconAdjustVisuals from '../../../../shared/ui/icons/IconAdjustVisuals';
import IconLayout from '../../../../shared/ui/icons/IconLayout';
import IconResize from '../../../../shared/ui/icons/IconResize';
import IconEffects from '../../../../shared/ui/icons/IconEffects';
import IconImagePrompt from '../../../../shared/ui/icons/IconImagePrompt';
import IconWizardHat from '../../../../shared/ui/icons/IconWizardHat';
import IconAddText from '../../../../shared/ui/icons/IconAddText';
import IconEnhance from '../../../../shared/ui/icons/IconEnhance';

import { useAppDispatch } from '../../../../store/store';
import {} from '../../../../shared/helpers/imageHelpers';

type PromptPanelType = 'text' | 'image';

interface ChoosePromptPanelProps {
    type: PromptPanelType;
}

const BLOCKS: Array<{
    name: BlockName;
    label: string;
    icon: ReactElement;
    disabled: boolean;
    target: PromptPanelType;
    isPremium: boolean;
}> = [
    {
        name: 'tone',
        label: 'Adjust tone',
        icon: <IconTone color="#BDBDBD" />,
        disabled: false,
        target: 'text',
        isPremium: false,
    },
    {
        name: 'voice',
        label: 'Adjust voice',
        icon: <IconVoice color="#BDBDBD" />,
        disabled: false,
        target: 'text',
        isPremium: false,
    },
    {
        name: 'improve',
        label: 'Improve text',
        icon: <IconImproveText color="#BDBDBD" />,
        disabled: false,
        target: 'text',
        isPremium: true,
    },
    {
        name: 'edit',
        label: 'Edit prompt',
        icon: <IconEditPrompt color="#000000" />,
        disabled: false,
        target: 'text',
        isPremium: false,
    },
    {
        name: 'suggestions',
        label: 'Suggestions',
        icon: <IconSuggestions color="#BDBDBD" />,
        disabled: true,
        target: 'text',
        isPremium: true,
    },
    {
        name: 'references',
        label: 'References',
        icon: <IconReferences color="#BDBDBD" />,
        disabled: false,
        target: 'text',
        isPremium: true,
    },
    {
        name: 'hashtags',
        label: 'Hashtags',
        icon: <IconHashtagsx />,
        disabled: true,
        target: 'text',
        isPremium: true,
    },
    {
        name: 'upload',
        label: 'Upload',
        icon: <IconUpload color="#000000" />,
        disabled: false,
        target: 'text',
        isPremium: false,
    },
    {
        name: 'chat',
        label: 'ChatBot',
        icon: <IconChatBot color="#BDBDBD" />,
        disabled: true,
        target: 'text',
        isPremium: true,
    },
    {
        name: 'adjust_visuals',
        label: 'Adjust visuals',
        icon: <IconAdjustVisuals />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'layout',
        label: 'Layout',
        icon: <IconLayout />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'resize',
        label: 'Adjust size & shape',
        icon: <IconResize />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'effects',
        label: 'Effects',
        icon: <IconEffects />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'image_prompt',
        label: 'Image prompt',
        icon: <IconImagePrompt />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'merger',
        label: 'Image Wizard',
        icon: <IconWizardHat />,
        disabled: false,
        target: 'image',
        isPremium: true,
    },
    {
        name: 'add_text',
        label: 'Add text',
        icon: <IconAddText />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'image_uploads',
        label: 'Uploads',
        icon: <IconUpload />,
        disabled: false,
        target: 'image',
        isPremium: false,
    },
    {
        name: 'enhance',
        label: 'Enhance',
        icon: <IconEnhance />,
        disabled: false,
        target: 'image',
        isPremium: true,
    },
];

const ChoosePromptPanel: FC<ChoosePromptPanelProps> = ({ type }) => {
    const activePrompt = useSelector(selectActivePrompt);
    const dispatch = useAppDispatch();

    const handleToggleBlock = async (block: BlockName) => {
        dispatch(toggleActivePrompt(block));
    };

    return (
        <div className={type === 'image' ? s.image_container : s.container}>
            <p className={s.header}>
                {type === 'image'
                    ? 'Choose how to edit your image'
                    : 'Choose how to customize your content'}
            </p>
            <div
                className={type == 'image' ? s.prompt_image_container : s.prompt_section_container}
            >
                {BLOCKS.filter((block) => block.target === type).map(
                    ({ name, label, icon, disabled, isPremium }) => (
                        <div
                            key={name}
                            className={`
                                ${s.prompt_item} 
                                ${activePrompt === name ? s.active : ''} 
                                ${disabled ? s.disabled : ''}
                            `}
                            onClick={() => !disabled && handleToggleBlock(name)}
                        >
                            {icon}
                            <p className={s.text}>{label}</p>
                            {!disabled && isPremium && (
                                <div className={`${s.diamond_wrapper}`}>
                                    <PremiumFeatureTooltip />
                                </div>
                            )}

                            {disabled && (
                                <div className={`${s.diamond_wrapper}`}>
                                    <PremiumFeatureTooltip />
                                </div>
                            )}
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};

export default ChoosePromptPanel;
